import { useCallback, useState, type KeyboardEvent } from "react";

/**
 * Search state that only fires after explicit commit (Enter key or button click).
 * Returns draft (for the input) and committed value (for the query).
 */
export function useCommittedSearch(initialCommitted = "") {
  const [draft, setDraft] = useState(initialCommitted);
  const [committed, setCommitted] = useState(initialCommitted);

  const commit = useCallback(() => {
    setCommitted(draft.trim());
  }, [draft]);

  const clear = useCallback(() => {
    setDraft("");
    setCommitted("");
  }, []);

  const hydrate = useCallback((value: string) => {
    const trimmed = value.trim();
    setDraft(trimmed);
    setCommitted(trimmed);
  }, []);

  const onKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const next = e.currentTarget.value.trim();
      if (next.length > 0) {
        setCommitted(next);
      }
    }
  }, []);

  const hasUnapplied = draft.trim() !== committed;

  return {
    draft,
    setDraft,
    committed,
    commit,
    clear,
    hydrate,
    onKeyDown,
    hasUnapplied,
  };
}
